// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Observable, defer, forkJoin, from, map, of, switchMap, throwError } from 'rxjs';
import { FileMeta, HceApiService } from '../hce-api/hce-api.service';
import { HceDataCacheService } from '../hce-data-cache/hce-data-cache.service';
import type { HttpOptions } from '@zip.js/zip.js';

// ===== API response types =====

interface SignedUrlResponse {
  success: boolean;
  message?: string;
  signed_url?: string | null;
}

interface FolderAttributes {
  folder_id: string;
  folder_name: string;
  time_created: string; // ISO 8601 timestamp
  updated: string; // ISO 8601 timestamp
  labels: Record<string, string>;
  created_by: string;
  project_name: string;
}

interface FoldersResponse {
  success: boolean;
  message?: string;
  folders?: FolderAttributes[];
}

interface FileAttributes {
  file_name: string;
  full_path: string;
  time_created: string; // ISO 8601 timestamp
  size: number;
}

interface FilesResponse {
  success: boolean;
  message?: string;
  files?: FileAttributes[];
}

export type HceResponseType = 'arraybuffer' | 'blob' | 'text';

@Injectable({
  providedIn: 'root'
})
export class HceFileManagementService {
  // ===== Constants / Private fields =====
  private readonly apiPath = '/v1';

  private readonly gzRegex = /\.gz(?:$|\?)/i;
  private readonly zipRegex = /\.zip(?:$|\?)/i;
  private readonly annotationRegex = /\.(bed|vcf)$/i;

  // ===== DI constructor =====
  constructor(
    private readonly api: HceApiService,
    private readonly cache: HceDataCacheService,
  ) {}

  // ===== Public API =====

  public getFolders(folderPath: string): Observable<FolderAttributes[]> {
    const url = this.makeUrl(`/buckets/folders/${folderPath}`);
    return this.api.get<FoldersResponse>(url).pipe(
      map(response => {
        if (!response?.success) {
          throw new Error(response?.message ?? `Failed to fetch folders/${folderPath}`);
        }
        let folders: FolderAttributes[] = [];
        if (response.folders) {
          if ('folders' in response.folders) {
            folders = (response.folders as any).folders;
          } else {
            folders = response.folders;
          }
          if (Array.isArray(folders)) {
            folders.map(folder => {
              const idx = folder.folder_name.lastIndexOf('/');
              if (idx !== -1) {
                folder.folder_name = folder.folder_name.slice(idx + 1);
              }
            })
          }
        }
        return folders;
      }),
    );
  }

  public existsFolder(folderPath: string): Observable<boolean> {
    const pathSegments = folderPath.split('/').filter(Boolean);
    const targetFolderName = (pathSegments.pop() ?? '').toLowerCase();
    const parentPath = pathSegments.join('/');
    return this.getFolders(parentPath).pipe(
      map(folders => 
        folders
          .map(folder => folder.folder_name.toLowerCase())
          .includes(targetFolderName)
      ),
    );
  }

  public getFiles(folderPath: string): Observable<FileAttributes[]> {
    const url = this.makeUrl(`/buckets/files/${folderPath}`);
    return this.api.get<FilesResponse>(url).pipe(
      map(response => {
        if (!response?.success) {
          throw new Error(response?.message ?? `Failed to fetch files/${folderPath}`);
        }
        return response.files ?? [];
      }),
    );
  }

  public getFilesMeta(folderPath: string, filenamePattern?: RegExp): Observable<FileMeta[]> {
    return this.getFiles(folderPath).pipe(
      map(files =>
        files
          .filter(file => !filenamePattern || filenamePattern.test(file.file_name))
          .map(file => ({
            filename: file.file_name,
            size: file.size,
            source: 'attributes',
          }))
      )
    );
  }

  public getAnalysisSignedUrl(analysisId: number, filename: string): Observable<string> {
    const url = this.makeUrl('/buckets/download/analysis', {
      analysis_id: analysisId.toString(),
      filename,
    });
    return this.getSignedUrl(url);
  }

  public getCommonSignedUrl(filename: string): Observable<string> {
    const url = this.makeUrl('/buckets/download', {
      common: 'true',
      filename,
    });
    return this.getSignedUrl(url);
  }

  /** Get analysis file content (with caching). */
  public getAnalysisFile(analysisId: number, filename: string, responseType: HceResponseType): Observable<ArrayBuffer | Blob | string>;
  public getAnalysisFile(analysisId: number, filename: string, responseType: 'arraybuffer'): Observable<ArrayBuffer>;
  public getAnalysisFile(analysisId: number, filename: string, responseType: 'blob'): Observable<Blob>;
  public getAnalysisFile(analysisId: number, filename: string, responseType: 'text'): Observable<string>;
  public getAnalysisFile(analysisId: number, filename: string): Observable<string>;
  public getAnalysisFile(
    analysisId: number,
    filename: string,
    responseType: HceResponseType = 'text',
  ): Observable<ArrayBuffer | Blob | string> {
    const url = this.makeUrl('/buckets/download/analysis', {
      analysis_id: analysisId.toString(),
      filename,
    });
    return this.getFile(url, filename, responseType);
  }

  /** Get common file content (with caching). */
  public getCommonFile(filename: string, responseType: HceResponseType): Observable<ArrayBuffer | Blob | string>;
  public getCommonFile(filename: string, responseType: 'arraybuffer'): Observable<ArrayBuffer>;
  public getCommonFile(filename: string, responseType: 'blob'): Observable<Blob>;
  public getCommonFile(filename: string, responseType: 'text'): Observable<string>;
  public getCommonFile(filename: string): Observable<string>;
  public getCommonFile(
    filename: string,
    responseType: HceResponseType = 'text',
  ): Observable<ArrayBuffer | Blob | string> {
    const url = this.makeUrl('/buckets/download', {
      common: 'true',
      filename,
    });
    return this.getFile(url, filename, responseType);
  }

  /** Get annotation file content. */
  public getAnnotationFile(): Observable<Record<string, string>> {
    return this.getFilesMeta('FASTA').pipe(
      map(files => files.filter(f => this.annotationRegex.test(f.filename))),
      switchMap(files => {
        if (files.length === 0) {
          return of({});
        }
        return forkJoin(
          Object.fromEntries(
            files.map(meta => {
              const full = `FASTA/${meta.filename}`;
              const url = this.makeUrl('/buckets/download', {
                common: 'false',
                filename: full,
              });
              return [
                meta.filename,
                this.getFile(url, full, 'text') as Observable<string>
              ] as const;
            })
          )
        )
      }),
    );
  }

  /** Get panel file content (with caching). */
  public getPanelFile(
    panelId: number,
    innerPath: string
  ): Observable<string> {
    const url = this.makeUrl(`/buckets/download/panel/${panelId}`, {
      filetype: "raw",
    });
    const cacheKey = this.makeZipCacheKey(url, innerPath);

    return this.tryCacheGet(cacheKey).pipe(
      switchMap(cached => {
        if (cached != null) {
          return of(cached);
        }
        return this.getSignedUrl(url).pipe(
          switchMap(signedUrl => 
            this.getZipEntryBuffer$(signedUrl, innerPath).pipe(
              switchMap((ab) => {
                if (ab == null) {
                  return throwError(() => new Error(`ZIP entry not found: ${innerPath}`));
                };
                if (this.isGz(innerPath)) {
                  return this.gunzipToText$(ab);
                }
                const text = new TextDecoder('utf-8').decode(new Uint8Array(ab));
                return of<string>(text);
              }),
              switchMap(text => 
                this.cachePut(cacheKey, text).pipe(map(() => text))
              )
            )
          )
        );
      })
    );
  }

  // ===== Private core helpers =====

  private getFile(
    url: string,
    filename: string,
    responseType: HceResponseType,
  ): Observable<string | ArrayBuffer | Blob> {
    if (responseType === 'text') {
      return this.tryCacheGet(url).pipe(
        switchMap(cached => {
          if (cached != null) {
            return of(cached);
          }
          return this.getSignedUrl(url).pipe(
            switchMap(signedUrl => this.fetchArrayBuffer(signedUrl)),
            switchMap(ab => this.toFinalText(ab, filename)),
            switchMap(text => this.cachePut(url, text).pipe(map(() => text)))
          );
        })
      );
    }
    return this.getSignedUrl(url).pipe(
      switchMap(signedUrl => {
        if (responseType === 'blob') {
          return this.fetchBlob(signedUrl);
        }
        return this.fetchArrayBuffer(signedUrl);
      })
    );
  }

  private getSignedUrl(url: string): Observable<string> {
    return this.api
      .get<SignedUrlResponse>(url)
      .pipe(map((response) => {
        if (response?.success && typeof response.signed_url === 'string') {
          return response.signed_url;
        }
        throw new Error(response?.message ?? 'Could not retrieve signed URL.');
      }));
  }

  private makeUrl(
    pathname: string,
    params?: { [key: string]: string }
  ): string {
    let path = `${this.apiPath}${pathname}`;
    if (params) {
      const search = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        search.set(key, value);
      }
      path += `?${search.toString()}`;
    }
    return path;
  }

  private makeZipCacheKey(url: string, innerPath: string): string {
    return `${url}#${innerPath}`;
  }

  private tryCacheGet(url: string): Observable<string | null> {
    return from(this.cache.get(url));
  }

  private cachePut(url: string, text: string): Observable<void> {
    return from(this.cache.put(url, text));
  }

  private fetchArrayBuffer(signedUrl: string): Observable<ArrayBuffer> {
    return this.api.getArrayBuffer(signedUrl).pipe(
      switchMap(ab => {
        if (ab === null) {
          return throwError(() => new Error('No content received.'));
        }
        return of(ab);
      })
    );
  }

  private fetchBlob(signedUrl: string): Observable<Blob> {
    return this.api.getBlob(signedUrl).pipe(
      switchMap(blob => {
        if (blob === null) {
          return throwError(() => new Error('No blob content received.'));
        }
        return of(blob);
      })
    );
  }

  private toFinalText(
    ab: ArrayBuffer,
    filename: string,
  ): Observable<string> {
    if (this.isGz(filename)) {
      return this.gunzipToText$(ab);
    }
    if (this.isZip(filename)) {
      return throwError(() => new Error('ZIP format is unsupported here.'));
    }
    return of(new TextDecoder('utf-8').decode(new Uint8Array(ab)));
  }

  private isGz(filename: string): boolean {
    return this.gzRegex.test(filename);
  }

  private isZip(filename: string): boolean {
    return this.zipRegex.test(filename);
  }

  private gunzipToText$(ab: ArrayBuffer, encoding: string = 'utf-8'): Observable<string> {
    return defer(() => {
      if (typeof (globalThis as any).DecompressionStream === 'function') {
        const ds = new (globalThis as any).DecompressionStream('gzip');
        const stream = new Blob([ab]).stream().pipeThrough(ds);
        if (['utf-8', 'utf8'].includes(encoding.toLowerCase())) {
          return from(new Response(stream).text());
        }
        return from(new Response(stream).arrayBuffer()).pipe(
          map((buf) => new TextDecoder(encoding).decode(new Uint8Array(buf)))
        );
      }
      throw new Error('No gzip decompressor available. Enable DecompressionStream or install pako.');
    });
  }

  /** ZIP entry extraction */
  private getZipEntryBuffer$(
    signedUrl: string,
    innerPath: string
  ): Observable<ArrayBuffer | null> {
    return defer(async () => {
      const {
        ZipReader,
        HttpRangeReader,
        HttpReader,
        BlobReader,
        Uint8ArrayWriter,
      } = await this.importZipJs();

      const normalize = (s: string) => s.replace(/^\/+|\/+$/g, '');
      const wanted = normalize(innerPath);

      const extract = async (readerImpl: any): Promise<ArrayBuffer | null> => {
        const reader = new ZipReader(readerImpl);
        try {
          const entries = await reader.getEntries();
          const entry = entries.find(e => normalize(e.filename).endsWith(wanted));
          if (!entry || entry.directory === true) {
            return null;
          }
          const u8 = await entry.getData(new Uint8ArrayWriter());
          return (u8 as Uint8Array).buffer;
        } finally {
          try { await reader.close(); } catch {}
        }
      };

      try {
        const options: HttpOptions = { preventHeadRequest: false };
        return await extract(new HttpRangeReader(signedUrl, options));
      } catch {}

      try {
        return await extract(new HttpReader(signedUrl));
      } catch (err) {}

      const res = await fetch(signedUrl, { method: 'GET' });
      if (!res.ok) {
        throw new Error(`fetch failed ${res.status}`);
      }
      const blob = await res.blob();
      return await extract(new BlobReader(blob));
    });
  }

  private importZipJs() {
    return import('@zip.js/zip.js');
  }
}